import React, { useState, useEffect, useRef } from "react";
import TopNavbar from "../top-navbar/TopNav";
import Sidebar from "./Sidebar";
import { getdigilocker, downloadAadhaar, getS3key } from "../../services/employee/api";
import { environment } from "../../config/environment";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import "./AadhaarCheck.css";

const AadhaarCheck = () => {
  const [loading, setLoading] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const id = localStorage.getItem("QuickBGC_candidateID");
  const [aadhaarLoading, setAadhaarLoading] = useState(true);
  const [hasAadhaarData, setHasAadhaarData] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchAadhaar = async () => {
      if (isFetchingRef.current) return;

      const localClientId = localStorage.getItem("quickbgv_digilockerClientId");
      const candidateId = localStorage.getItem("QuickBGC_candidateID");

      if (!localClientId || !candidateId) {
        setAadhaarLoading(false);
        return;
      }

      try {
        isFetchingRef.current = true;
        setAadhaarLoading(true);

        const payload = {
          candidateId: candidateId,
          clientId: localClientId,
        };

        const response = await downloadAadhaar(payload);

        if (response?.data?.status === true && response?.data?.data) {
          const aadhaarData = response.data.data;

          if (aadhaarData?.name || aadhaarData?.fullName) {
            setCandidateData(aadhaarData);
            setHasAadhaarData(true);
            loadProfileImage(aadhaarData.profileImageUrl);
          } else {
            setHasAadhaarData(false);
            setCandidateData(null);
            toast.warn("Identity details incomplete");
          }
        } else {
          setHasAadhaarData(false);
          setCandidateData(null);
        }
      } catch (error) {
        const errorMsg = error?.response?.data?.message || "Failed to fetch details";
        toast.error(errorMsg);
        setHasAadhaarData(false);
        setCandidateData(null);
      } finally {
        setAadhaarLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchAadhaar();
  }, []);

  const loadProfileImage = async (fileUrl) => {
    try {
      if (!fileUrl) return;
      let key = fileUrl;
      if (key.includes(".com/")) key = key.split(".com/")[1];
      key = decodeURIComponent(key);

      const response = await getS3key(key);
      setProfileImage(URL.createObjectURL(response.data));
    } catch (error) {
      console.error("Error loading profile image:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (profileImage?.startsWith("blob:")) URL.revokeObjectURL(profileImage);
    };
  }, [profileImage]);

  const handleBack = () => {
    if (id) {
            toast.success("Details saved successfully!");
      navigate(`/employee/quickbgv-dashboard/${id}`);
    } else {
      navigate(-1);
    }
  };

  const handleVerify = async () => {
    try {
      setLoading(true);
      localStorage.setItem(
        "redirectAfterDigilocker",
        window.location.pathname + window.location.search
      );

      const payload = {
        apiType: "digilockerurl",
        data: {
          signupFlow: true,
          redirectUrl:
           `${environment.apiUrl}/quickdigilocker/callback`,
        },
      };

      const response = await getdigilocker(payload);

      if (response?.data?.success) {
        const redirectUrl = response.data.data.url;

        localStorage.setItem(
          "quickbgv_digilockerClientId",
          response.data.data.client_id
        );

        window.location.href = redirectUrl;
      }
    } catch (err) {
      toast.error("Failed to initiate DigiLocker verification");
    } finally {
      setLoading(false);
    }
  };

  // Dedicated function to explicitly save to DB on click
  const handleSaveDetails = async () => {
    try {
      setLoading(true);

      const savePayload = {
        candidateId: id,
        clientId: localStorage.getItem("quickbgv_digilockerClientId"),
        details: candidateData,
        status: "COMPLETED",
      };

      // TODO: Replace 'saveAadhaarDetails' with your actual API endpoint to save data & update DB status
      // await saveAadhaarDetails(savePayload);

      toast.success("Details saved successfully!");

      // Clear local reference states
      setCandidateData(null);
      setHasAadhaarData(false);

      if (id) {
        navigate(`/employee/quickbgv-dashboard/${id}`);
      } else {
        navigate("/employee/aadhaarcheck");
      }
    } catch (error) {
      toast.error("Failed to save details to database");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-app-container">
      <Sidebar />

      <div className="dashboard-right-panel">
        <TopNavbar />
        <div className="container-fluid py-4">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow border-0">
                <div className="quickbgv-uan-header bg-white rounded-top">
                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="quick-btn-back-custom p-0 d-flex align-items-center justify-content-center"
                      onClick={handleBack}
                      title="Go Back"
                    >
                      <FaArrowLeft size={16} />
                    </button>

                    <div className="flex-grow-1">
                      <h3
                        className="fw-bold mb-1"
                        style={{ color: "#4a154b", fontSize: "1.25rem" }}
                      >
                        Identity Verification
                      </h3>
                      <p
                        className="text-muted mb-0"
                        style={{ fontSize: "0.875rem" }}
                      >
                        Verify candidate identity details using DigiLocker records.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {aadhaarLoading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Fetching Identity Details...</p>
                    </div>
                  ) : hasAadhaarData && candidateData ? (
                    <div className="candidate-container">
                      <div className="candidate-header">
                        <div className="header-title">
                          <i className="fas fa-id-card"></i>
                          <h4>Candidate Details</h4>
                        </div>
                        <span className="verification-badge">
                          <i className="fas fa-check-circle"></i>
                          Verified
                        </span>
                      </div>

                      <div className="profile-section">
                        <img
                          src={profileImage || "/placeholder-avatar.png"}
                          className="candidate-profile-image"
                          alt=""
                        />
                        <h3>{candidateData.name || candidateData.fullName}</h3>
                        <div className="aadhaar-number">
                          {candidateData.maskedAadhaar || candidateData.aadhaarNumber || "Verified"}
                        </div>
                      </div>

                      <div className="row g-4">
                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>Identity Number</label>
                            <h5>{candidateData.maskedAadhaar || candidateData.aadhaarNumber || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>Date of Birth</label>
                            <h5>{candidateData.dob || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>Gender</label>
                            <h5>{candidateData.gender || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>Father Name</label>
                            <h5>{candidateData.fatherName || candidateData.careOf || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>Country</label>
                            <h5>{candidateData.country || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>State</label>
                            <h5>{candidateData.state || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-xl-4 col-lg-6 col-md-6">
                          <div className="info-card">
                            <label>District</label>
                            <h5>{candidateData.district || "N/A"}</h5>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="info-card">
                            <label>Address</label>
                            <h5>{candidateData.fullAddress || "N/A"}</h5>
                          </div>
                        </div>
                      </div>

                      <div className="save-btn-wrapper">
                        {/* CANCEL: Returns without saving status */}
                        {/* <button
                          type="button"
                          className="save-cancel-btn"
                          onClick={() => {
                            if (id) {
                              navigate(`/employee/quickbgv-dashboard/${id}`);
                            } else {
                              navigate(-1);
                            }
                          }}
                        >
                          Cancel
                        </button> */}

                        {/* SAVE DETAILS: Saves to DB and updates status */}
                        <button
                          type="button"
                          className="save-details-btn"
                          onClick={handleSaveDetails}
                          disabled={loading}
                        >
                          <i className="fas fa-save me-2"></i>
                          {loading ? "Saving..." : "Save Details"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted mb-1">
                        Click the button below to start the DigiLocker
                        verification process.
                      </p>
                      <p className="text-muted mb-4">
                        Once verified, candidate details will be displayed here.
                      </p>

                      <button
                        className="save-details-btn"
                        onClick={handleVerify}
                        disabled={loading}
                      >
                        {loading ? "Verifying..." : "Verify with DigiLocker"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AadhaarCheck;